import { Calendar, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@lean-agility/design-system/react';
import { Badge } from '@lean-agility/design-system/react';
import { DistributionChart } from './DistributionChart';
import { formatDate, isDueBeforeStart } from '@/lib/utils';
import type { ProcessedDeliverableResult } from '@/types/api';

export interface FeatureResultCardProps {
  result: ProcessedDeliverableResult;
  /** Team color (from database/form config) */
  teamColor: string;
  animationDelay?: number;
  /** Effective simulation start date; a due date before this predates the forecast (S3 warning). */
  simulationStartDate: Date;
}

export function FeatureResultCard({
  result,
  teamColor,
  animationDelay = 0,
  simulationStartDate,
}: FeatureResultCardProps) {
  const { finishPercentiles, atRisk, riskProbability } = result;
  const showFeatureName = !!result.feature_name;
  // A due date before the forecast start is impossible to meet — a stronger,
  // data-quality signal than "at risk" (which is a probabilistic miss).
  const dueBeforeStart = isDueBeforeStart(result.due_date, simulationStartDate);

  return (
    <Card className="animate-slide-up" style={{ animationDelay: `${animationDelay}ms` }}>
      <CardHeader
        className="flex flex-row items-start justify-between gap-4"
        style={{ borderLeftColor: teamColor, borderLeftWidth: '4px' }}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <CardTitle>{result.deliverable_name}</CardTitle>
            {dueBeforeStart ? (
              <Badge variant="danger" className="flex items-center gap-1">
                <AlertTriangle className="size-3" />
                Due before start
              </Badge>
            ) : atRisk ? (
              <Badge variant="danger" className="flex items-center gap-1">
                <AlertTriangle className="size-3" />
                At Risk
              </Badge>
            ) : result.due_date ? (
              <Badge variant="success" className="flex items-center gap-1">
                <CheckCircle className="size-3" />
                On Track
              </Badge>
            ) : null}
          </div>
          <p className="text-sm text-text-muted">
            {result.team_name}
            {showFeatureName && <span className="ml-2">• {result.feature_name}</span>}
          </p>
        </div>

        {result.due_date && (
          <div className="text-right">
            <div className="flex items-center gap-1 text-sm text-text-muted">
              <Calendar className="size-4" />
              Due: {formatDate(result.due_date)}
            </div>
            {dueBeforeStart ? (
              <p className="mt-1 text-xs text-feedback-error">Deadline predates forecast start</p>
            ) : (
              riskProbability !== null && (
                <p className="mt-1 text-xs text-text-muted">
                  {Math.round(riskProbability * 100)}% chance of missing deadline
                </p>
              )
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Percentile Summary */}
        <div className="grid grid-cols-3 gap-4">
          <div
            className="
              rounded-lg bg-bg-muted p-3 text-center
              dark:bg-bg-elevated/50
            "
          >
            <p className="mb-1 text-xs text-text-muted">50th Percentile</p>
            <p className="text-lg font-semibold text-text-primary">
              {finishPercentiles.p50 ? formatDate(finishPercentiles.p50) : 'N/A'}
            </p>
            <p className="text-xs text-text-muted">Half finish by</p>
          </div>
          <div
            className="
              rounded-lg bg-bg-muted p-3 text-center
              dark:bg-bg-elevated/50
            "
          >
            <p className="mb-1 text-xs text-text-muted">85th Percentile</p>
            <p className="text-lg font-semibold text-feedback-info">
              {finishPercentiles.p85 ? formatDate(finishPercentiles.p85) : 'N/A'}
            </p>
            <p className="text-xs text-text-muted">High confidence</p>
          </div>
          <div
            className="
              rounded-lg bg-bg-muted p-3 text-center
              dark:bg-bg-elevated/50
            "
          >
            <p className="mb-1 text-xs text-text-muted">95th Percentile</p>
            <p className="text-lg font-semibold text-feedback-warning">
              {finishPercentiles.p95 ? formatDate(finishPercentiles.p95) : 'N/A'}
            </p>
            <p className="text-xs text-text-muted">Very conservative</p>
          </div>
        </div>

        {/* Charts */}
        <div
          className="
            grid grid-cols-1 gap-6
            lg:grid-cols-2
          "
        >
          <DistributionChart
            data={result.started}
            color={teamColor}
            title="Start Date Distribution"
            showCumulative={false}
          />
          <DistributionChart
            data={result.finished}
            color={teamColor}
            dueDate={result.due_date}
            dueDateProbability={riskProbability !== null ? 1 - riskProbability : null}
            title="Finish Date Distribution"
            sourceId={result.source_id}
            showCumulative={true}
          />
        </div>
      </CardContent>
    </Card>
  );
}
